import React, { useState, useEffect } from 'react';
import { 
  Box, 
  Grid, 
  Card, 
  CardContent, 
  Typography, 
  CircularProgress,
  Paper,
} from '@mui/material';
import {
  Computer as ComputerIcon,
  Memory as MemoryIcon,
  Storage as StorageIcon,
  Speed as CpuIcon,
} from '@mui/icons-material';
import { PieChart, Pie, Cell, ResponsiveContainer, Tooltip, Legend } from 'recharts';
import { useCustomTheme } from '../contexts/ThemeContext';
import apiService from '../services/api';

const HomePage = () => {
  const { chartColors } = useCustomTheme();
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState({
    vms: { total: 0, running: 0 },
    gpus: { total: 0, available: 0 },
    volumes: { total: 0 }
  });
  const [resources, setResources] = useState(null);

  useEffect(() => {
    const fetchDashboardData = async () => {
      setLoading(true);
      try {
        // 병렬로 데이터 요청
        const [vmsResponse, gpusResponse, volumesResponse, resourcesResponse] = await Promise.all([
          apiService.getVMs(true),
          apiService.getGPUs(),
          apiService.getVolumes(),
          apiService.getHostResources()
        ]);

        const runningVMs = vmsResponse.data.vms.filter(vm => vm.state === 1);
        const availableGPUs = gpusResponse.data.gpus.filter(gpu => !gpu.assigned);
        
        setStats({
          vms: { 
            total: vmsResponse.data.vms.length, 
            running: runningVMs.length 
          },
          gpus: { 
            total: gpusResponse.data.gpus.length, 
            available: availableGPUs.length 
          },
          volumes: { 
            total: volumesResponse.data.volumes.length 
          }
        });

        setResources(resourcesResponse.data);
      } catch (error) {
        // 대시보드 데이터 로드 실패는 기본값 사용
      } finally {
        setLoading(false);
      }
    };

    fetchDashboardData();
  }, []);

  // 바이트를 사람이 읽기 쉬운 형태로 변환
  const formatBytes = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  // 도넛 차트 데이터 생성
  const createChartData = (used, total, label) => {
    const free = total - used;
    return [
      { name: `사용 중 ${label}`, value: used, color: chartColors.primary },
      { name: `사용 가능 ${label}`, value: free, color: chartColors.secondary }
    ];
  };

  // 도넛 차트 컴포넌트
  const ResourceChart = ({ title, data, formatValue, unit }) => {
    const usagePercentage = data.length > 0 ? ((data[0]?.value || 0) / data.reduce((sum, item) => sum + item.value, 0) * 100).toFixed(1) : 0;
    
    return (
      <Card sx={{ height: '100%' }}>
        <CardContent>
          <Typography variant="h6" gutterBottom align="center">
            {title}
          </Typography>
          <Box sx={{ position: 'relative' }}>
            <ResponsiveContainer width="100%" height={200}>
              <PieChart>
                <Pie
                  data={data}
                  cx="50%"
                  cy="50%"
                  innerRadius={50}
                  outerRadius={80}
                  dataKey="value"
                >
                  {data.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={entry.color} />
                  ))}
                </Pie>
                <Tooltip
                  formatter={(value) => [formatValue ? formatValue(value) : `${value}${unit || ''}`, '']}
                />
                <Legend />
              </PieChart>
            </ResponsiveContainer>
            
            {/* 중앙에 사용률 숫자만 표시 */}
            <Box
              sx={{
                position: 'absolute',
                top: '50%',
                left: '50%',
                transform: 'translate(-50%, -50%)',
                textAlign: 'center',
                pointerEvents: 'none'
              }}
            >
              <Typography 
                variant="h3" 
                component="div" 
                sx={{ 
                  fontWeight: 'bold',
                  color: 'primary.main',
                  lineHeight: 1,
                  fontSize: '2.5rem'
                }}
              >
                {usagePercentage}
              </Typography>
            </Box>
          </Box>
          
          <Box sx={{ mt: 2, textAlign: 'center' }}>
            <Typography variant="body2" color="text.secondary">
              사용량: {formatValue ? formatValue(data[0]?.value || 0) : `${data[0]?.value || 0}${unit || ''}`} / {formatValue ? formatValue(data.reduce((sum, item) => sum + item.value, 0)) : `${data.reduce((sum, item) => sum + item.value, 0)}${unit || ''}`}
            </Typography>
          </Box>
        </CardContent>
      </Card>
    );
  };

  const statCards = [
    {
      title: '가상머신',
      icon: <ComputerIcon sx={{ fontSize: 40, color: 'primary.main' }} />,
      primary: `${stats.vms.running} / ${stats.vms.total}`,
      secondary: '실행 중 / 전체',
    },
    {
      title: 'GPU',
      icon: <MemoryIcon sx={{ fontSize: 40, color: 'secondary.main' }} />,
      primary: `${stats.gpus.available} / ${stats.gpus.total}`,
      secondary: '사용 가능 / 전체',
    },
    {
      title: '디스크 볼륨',
      icon: <StorageIcon sx={{ fontSize: 40, color: 'info.main' }} />,
      primary: `${stats.volumes.total}`,
      secondary: '볼륨 개수',
    },
  ];

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', mt: 10 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Typography variant="h4" gutterBottom>
        시스템 현황
      </Typography>
      <Grid container spacing={3}>
        {statCards.map((card, index) => (
          <Grid item xs={12} sm={6} md={4} key={index}>
            <Card>
              <CardContent sx={{ textAlign: 'center' }}>
                <Box sx={{ mb: 2 }}>{card.icon}</Box>
                <Typography variant="h5" component="div" gutterBottom>
                  {card.title}
                </Typography>
                <Typography variant="h3" color="text.primary">
                  {card.primary}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {card.secondary}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        ))}

        {/* 리소스 사용률 차트 */}
        {resources && (
          <>
            <Grid item xs={12}>
              <Typography variant="h5" gutterBottom sx={{ mt: 2, mb: 2 }}>
                호스트 리소스 사용률
              </Typography>
            </Grid>
            
            <Grid item xs={12} md={4}>
              <ResourceChart
                title="CPU 사용률"
                data={[
                  { name: `사용 중 CPU`, value: resources.host.cpu.used_cores, color: chartColors.colors[0] },
                  { name: `사용 가능 CPU`, value: resources.host.cpu.total_cores - resources.host.cpu.used_cores, color: chartColors.colors[1] }
                ]}
                unit=" 코어"
              />
            </Grid>
            
            <Grid item xs={12} md={4}>
              <ResourceChart
                title="메모리 사용률"
                data={[
                  { name: `사용 중 메모리`, value: resources.host.memory.used_bytes, color: chartColors.colors[2] },
                  { name: `사용 가능 메모리`, value: resources.host.memory.total_bytes - resources.host.memory.used_bytes, color: chartColors.colors[3] }
                ]}
                formatValue={formatBytes}
              />
            </Grid>
            
            <Grid item xs={12} md={4}>
              <ResourceChart
                title="디스크 사용률"
                data={[
                  { name: `사용 중 디스크`, value: resources.host.disk.used_bytes, color: chartColors.colors[4] || chartColors.colors[0] },
                  { name: `사용 가능 디스크`, value: resources.host.disk.total_bytes - resources.host.disk.used_bytes, color: chartColors.colors[1] }
                ]}
                formatValue={formatBytes}
              />
            </Grid>

            {/* VM별 리소스 할당 정보 */}
            {resources.vms && resources.vms.length > 0 && (
              <>
                <Grid item xs={12}>
                  <Typography variant="h6" gutterBottom sx={{ mt: 2, mb: 1 }}>
                    VM별 리소스 할당 현황
                  </Typography>
                </Grid>
                <Grid item xs={12} md={4}>
                  <ResourceChart
                    title="VM CPU 할당"
                    data={[
                      { name: `VM 할당 CPU`, value: resources.host.cpu.used_cores, color: chartColors.accent },
                      { name: `미할당 CPU`, value: resources.host.cpu.total_cores - resources.host.cpu.used_cores, color: chartColors.colors[1] }
                    ]}
                    unit=" 코어"
                  />
                </Grid>
                <Grid item xs={12} md={4}>
                  <ResourceChart
                    title="VM 메모리 할당"
                    data={[
                      { name: `VM 할당 메모리`, value: resources.host.memory.vm_allocated_bytes, color: chartColors.accent },
                      { name: `미할당 메모리`, value: resources.host.memory.total_bytes - resources.host.memory.vm_allocated_bytes, color: chartColors.colors[3] }
                    ]}
                    formatValue={formatBytes}
                  />
                </Grid>
                <Grid item xs={12} md={4}>
                  <ResourceChart
                    title="VM 디스크 사용"
                    data={[
                      { name: `VM 사용 디스크`, value: resources.host.disk.vm_used_bytes, color: chartColors.accent },
                      { name: `사용 가능 디스크`, value: resources.host.disk.total_bytes - resources.host.disk.vm_used_bytes, color: chartColors.colors[1] }
                    ]}
                    formatValue={formatBytes}
                  />
                </Grid>
              </>
            )}
          </>
        )}

        <Grid item xs={12}>
          <Paper sx={{ p: 2 }}>
            <Typography variant="h5" gutterBottom>
              GPU KVM API 서버
            </Typography>
            <Typography variant="body1" paragraph>
              이 대시보드는 KVM 환경에서 GPU passthrough (NVIDIA GPU만 지원)를 지원하는 가상머신을 관리하기 위한 인터페이스입니다.
            </Typography>
            <Typography variant="body1" paragraph>
              왼쪽 메뉴를 통해 다양한 기능에 접근할 수 있습니다:
            </Typography>
            <Typography component="ul" sx={{ pl: 2 }}>
              <li>가상머신 목록 조회 및 관리</li>
              <li>GPU 할당 관리</li>
              <li>OS 이미지 업로드 및 관리</li>
              <li>디스크 볼륨 생성 및 관리</li>
            </Typography>
          </Paper>
        </Grid>
      </Grid>
    </Box>
  );
};

export default HomePage; 